import { SocialName, SOCIAL_NETWORK, SocialAccountDto } from "@cede/types";
import { API_ENDPOINT, firstLetterToUpperCase } from "@cede/utils";
import { useTelegramWidget } from "./useTelegramWidget";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useMutation, useMutationState, useQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

const REMOVE_SOCIAL_ACCOUNT_MUTATION_KEY = "remove-social-account-mutation";
const ADD_SOCIAL_ACCOUNT_MUTATION_KEY = "add-social-account-mutation";
const SOCIAL_ACCOUNTS_QUERY_KEY = "social-accounts";

/**
 * This hook is used to manage the social accounts of the hub user.
 * It provides the following features:
 * - Add a social account
 * - Remove a social account
 * - Get the social accounts of the hub user
 */
export const useSocialAccounts = () => {
  const { auth, hubUser } = useHubUser();
  const api = useApi();

  const {
    data: socialAccounts,
    isLoading: isLoadingSocialAccounts,
    refetch: refetchSocialAccounts,
  } = useQuery({
    queryKey: [SOCIAL_ACCOUNTS_QUERY_KEY, hubUser?.id],
    queryFn: () => api.getWithAuth(auth, API_ENDPOINT.HUB_SOCIALS_ACCOUNTS),
    enabled: !!hubUser?.id && !!auth,
  });
  const { linkTelegramAccount } = useTelegramWidget();

  /**
   * Telegram doesn't support OAuth flow, we need to use a special flow to link the account based on the
   * client-side and not server-side.
   *
   * The flow is:
   * - User clicks on the "Link Telegram" button
   * - The Telegram widget is triggered and the user is redirected to the Telegram authorization page
   * - User authorizes the app
   * - The Telegram widget sends back the user data to the frontend app
   * - The frontend app sends the user data to the Cede API (with the hash to ensure the data isn't tampered)
   */
  const addTelegramAccount = async () => {
    const telegramUser = await linkTelegramAccount();

    if (!telegramUser) {
      throw new Error("Failed to get Telegram user data");
    }

    const { success } = await api.postAuth(auth, API_ENDPOINT.HUB_SOCIAL_OAUTH_SPECIAL_TELEGRAM_CALLBACK, {
      bodyParams: telegramUser,
    });

    if (!success) {
      throw new Error("Telegram account linking failed");
    }
    await refetchSocialAccounts();
  };

  const addSocialAccount = async (socialName: SocialName) => {
    try {
      if (socialName === "telegram") {
        await addTelegramAccount();
      } else {
        const { url } = await api.getWithAuth(auth, API_ENDPOINT.HUB_SOCIAL_OAUTH_INIT, {
          queryParams: { socialName, successUrl: window.location.href },
        });

        window.open(url, "_blank");
      }
    } catch (error) {
      enqueueSnackbar(
        `${firstLetterToUpperCase(socialName)} account linking failed: ${
          error instanceof Error ? error.message : String(error)
        }`,
        { variant: "error" },
      );
    }
  };

  const removeSocialAccount = async (socialAccount: SocialAccountDto) => {
    const result = await api.deleteAuth(auth, API_ENDPOINT.HUB_SOCIAL_OAUTH_REMOVE, {
      queryParams: { socialAccountId: socialAccount.id.toString() },
    });

    if (!result.success) {
      throw new Error("Failed to unlink social account");
    }

    enqueueSnackbar(`${firstLetterToUpperCase(socialAccount.socialName)} account unlinked`, { variant: "success" });
    return undefined;
  };

  const removeSocialAccountMutation = useMutation({
    mutationKey: [REMOVE_SOCIAL_ACCOUNT_MUTATION_KEY],
    mutationFn: removeSocialAccount,
    onSuccess: refetchSocialAccounts,
    onError: (error) => {
      enqueueSnackbar(`Failed to unlink social account: ${error instanceof Error ? error.message : String(error)}`, {
        variant: "error",
      });
    },
  });

  const removeSocialAccountMutationStates = useMutationState({
    filters: { status: "pending", mutationKey: [REMOVE_SOCIAL_ACCOUNT_MUTATION_KEY] },
  });

  const isRemovingSocialAccount = (id: number) =>
    removeSocialAccountMutationStates.some((state) => state.variables === id);

  const addSocialAccountMutation = useMutation({
    mutationKey: [ADD_SOCIAL_ACCOUNT_MUTATION_KEY],
    mutationFn: addSocialAccount,
  });

  const addSocialAccountMutationStates = useMutationState({
    filters: { status: "pending", mutationKey: [ADD_SOCIAL_ACCOUNT_MUTATION_KEY] },
  });

  const isAddingSocialAccount = (name: SocialName) =>
    addSocialAccountMutationStates.some((state) => state.variables === name);

  const hasRequiredSocialAccounts = (socialAccountsRequired: SocialName[]) => {
    if (socialAccountsRequired.length === 0) {
      return true;
    }
    // we check if the user has at least one of the required social accounts
    return socialAccountsRequired.some((socialName) => {
      return socialAccounts?.some((sa) => {
        if (socialName === SOCIAL_NETWORK.TELEGRAM) {
          return sa.socialName === SOCIAL_NETWORK.TELEGRAM || sa.socialName === SOCIAL_NETWORK.NATIVE_TELEGRAM;
        }
        return sa.socialName === socialName;
      });
    });
  };

  return {
    socialAccounts: socialAccounts ?? [],
    addSocialAccount: addSocialAccountMutation,
    removeSocialAccount: removeSocialAccountMutation,
    isLoading: isLoadingSocialAccounts || !socialAccounts,
    isRemovingSocialAccount,
    isAddingSocialAccount,
    hasRequiredSocialAccounts,
    refetchSocialAccounts,
  };
};
